import React, { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ScanProvider, useScan } from './contexts/ScanContext';
import { CookieConsentProvider, useCookieConsent } from './contexts/CookieConsentContext';
import { AnnouncementProvider } from './contexts/AnnouncementContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAnalytics, initializeAnalytics, updateAnalyticsConsent } from './services/analytics';
import AppShell from './components/AppShell';
import CookieConsentBanner from './components/CookieConsentBanner';
import DevToolbar from './components/DevToolbar';
import FeatureAnnouncement from './components/FeatureAnnouncement';
import SEO from './components/SEO';
import Home from './pages/Home.tsx';
import Search from './pages/Search.tsx';
import DomainDetail from './pages/DomainDetail.tsx';
import Announcement from './pages/Announcement.tsx';
import Guide from './pages/Guide.tsx';
import Resources from './pages/Resources.tsx';
import About from './pages/About.tsx';
import TopKeywords from './pages/TopKeywords.tsx';
import ExpiringDomains from './pages/ExpiringDomains.tsx';
import DesignSystem from './pages/DesignSystem.tsx';
import RadarStatus from './pages/RadarStatus.tsx';
import RadarNews from './pages/RadarNews.tsx';
import Watchlist from './pages/Watchlist.tsx';
import AuthCallback from './pages/AuthCallback.tsx';
import FAQ from './pages/FAQ.tsx';
import { useAnnouncement } from './contexts/AnnouncementContext';
import { WatchlistProvider, useWatchlistContext } from './contexts/WatchlistContext';

const FirstWatchTip: React.FC = () => {
  const { showFirstWatchTip, dismissFirstWatchTip } = useWatchlistContext();
  if (!showFirstWatchTip) return null;
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg cursor-pointer max-w-xs text-center"
      onClick={dismissFirstWatchTip}
    >
      Saved. Come back any time at{' '}
      <span className="text-blue-300 font-semibold">/watchlist</span> — even from a new tab.
    </div>
  );
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const { state } = useScan();
  const { consent, hasConsented } = useCookieConsent();
  const { showAnnouncement, dismissAnnouncement } = useAnnouncement();
  const analytics = useAnalytics();
  const resultsCount = Array.isArray(state.results) ? state.results.length : 0;
  const hasResults = resultsCount > 0;
  const isScanning = state.isLoading;

  useEffect(() => {
    if (hasConsented && consent) {
      initializeAnalytics(consent.analytics);
      updateAnalyticsConsent(consent.analytics);
    }
  }, [hasConsented, consent]);

  useEffect(() => {
    if (location.pathname === '/' && (hasResults || isScanning)) {
      if (hasResults) {
        analytics.pageViewed('Results Page', { results_count: resultsCount, searched_domain: state.searchedDomain });
      } else {
        analytics.pageViewed('Scanning Page', { searched_domain: state.searchedDomain, progress: state.progress.current });
      }
    } else if (location.pathname === '/guide') {
      analytics.pageViewed('Guide Page');
    } else if (location.pathname === '/resources') {
      analytics.pageViewed('Resources Page');
    } else if (location.pathname === '/about') {
      analytics.pageViewed('About Page');
    } else if (location.pathname === '/expiring') {
      analytics.pageViewed('Expiring Domains Page');
    } else {
      analytics.pageViewed('Home Page');
    }
  }, [location.pathname, hasResults, isScanning, resultsCount, state.searchedDomain, state.progress.current, analytics]);

  useEffect(() => {
    if (!isScanning && hasResults && resultsCount > 0) {
      analytics.searchCompleted('single', resultsCount);
    }
  }, [isScanning, hasResults, resultsCount, analytics]);

  return (
    <AppShell>
      <SEO
        results={Array.isArray(state.results) ? state.results : []}
        searchedDomain={state.searchedDomain || undefined}
      />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />} />
        <Route path="/domain/:domain" element={<DomainDetail />} />
        {/* Legacy query-param URL — redirect to semantic path */}
        <Route path="/domain" element={<DomainDetail />} />
        <Route path="/announcement" element={<Announcement />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/keywords" element={<TopKeywords />} />
        <Route path="/expiring" element={<ExpiringDomains />} />
        <Route path="/design-system" element={<DesignSystem />} />
        <Route path="/radar" element={<Navigate to="/radar/status" replace />} />
        <Route path="/radar/status" element={<RadarStatus />} />
        <Route path="/radar/news" element={<RadarNews />} />
        <Route path="/watchlist" element={<Watchlist />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
      </Routes>

      {import.meta.env.PROD && <Analytics />}
      {import.meta.env.PROD && <SpeedInsights />}
      <CookieConsentBanner />
      {import.meta.env.DEV && <DevToolbar />}
      {showAnnouncement && location.pathname !== '/' && <FeatureAnnouncement variant="popup" onDismiss={dismissAnnouncement} />}
      <FirstWatchTip />
    </AppShell>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <CookieConsentProvider>
        <AnnouncementProvider>
          <ScanProvider>
            <WatchlistProvider>
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </WatchlistProvider>
          </ScanProvider>
        </AnnouncementProvider>
      </CookieConsentProvider>
    </AuthProvider>
  );
};

export default App;
